<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { InformationCircleIcon } from '@heroicons/vue/24/outline'
  import type {
    ActaportPartyMatchingResponse,
    ActaportPartyWithMatches
  } from '@/types/generated/types.gen'
  import { ActaportPartyMatchingService } from '@/types/generated/services.gen'
  import PartyCard from './PartyCard.vue'
  import MatchCard from './MatchCard.vue'
  import PartyMatchingZeroState from './PartyMatchingZeroState.vue'
  import { useTranslation } from '@/composables/useTranslation'

  const props = defineProps<{
    caseUuid: string
    selectedParticipantUuids: string[]
    matchingResult?: ActaportPartyMatchingResponse | null
  }>()

  const emit = defineEmits<{
    (e: 'matching-complete', linkSelections: Map<string, string | 'create_new'>): void
    (e: 'skip'): void
    (e: 'back'): void
  }>()

  const { t } = useTranslation({
    en: {
      title: 'Beteiligte Match Check and Duplicate Prevention',
      subtitle:
        'We found {count} potential matches in Actaport. Please select one to link or create a new entity:',
      infoNote:
        'Please note that linking a party will not update any information in either Actaport or Jupus.',
      selectParticipant: 'Select a participant from the left panel.',
      progressCounter: "You've handled {handled} out of {total} potential duplicates.",
      allReviewed: 'All potential duplicates reviewed',
      potentialMatchesInActaport: 'Potential matches in Actaport',
      createNewBeteiligte: 'Create a New Beteiligte',
      create: 'Create',
      link: 'Link',
      saveTheChange: 'Save the change',
      back: 'Back',
      export: 'Export',
      loading: 'Loading matches...',
      noMatchesFound: 'No potential duplicates found. You can proceed with export.'
    },
    de: {
      title: 'Beteiligte Match Check und Duplikatprävention',
      subtitle:
        'Wir haben {count} potenzielle Treffer in Actaport gefunden. Bitte wählen Sie einen aus, um zu verknüpfen, oder erstellen Sie eine neue Entität:',
      infoNote:
        'Bitte beachten Sie, dass das Verknüpfen einer Partei keine Informationen in Actaport oder Jupus aktualisiert.',
      selectParticipant: 'Wählen Sie einen Teilnehmer aus dem linken Panel.',
      progressCounter: 'Sie haben {handled} von {total} potenziellen Duplikaten bearbeitet.',
      allReviewed: 'Alle potenziellen Duplikate überprüft',
      potentialMatchesInActaport: 'Potenzielle Treffer in Actaport',
      createNewBeteiligte: 'Neue Beteiligte erstellen',
      create: 'Erstellen',
      link: 'Verknüpfen',
      saveTheChange: 'Änderung speichern',
      back: 'Zurück',
      export: 'Exportieren',
      loading: 'Treffer werden geladen...',
      noMatchesFound: 'Keine potenziellen Duplikate gefunden. Sie können mit dem Export fortfahren.'
    }
  })

  const isLoading = ref(false)
  const matchingData = ref<ActaportPartyMatchingResponse | null>(props.matchingResult || null)
  const selectedPartyIndex = ref<number | null>(null)
  const selectedMatchId = ref<string | null>(null)

  // Track user decisions: entityUuid -> actaportContactId or 'create_new'
  const linkSelections = ref<Map<string, string | 'create_new'>>(new Map())

  const partiesWithMatches = computed(() => {
    return matchingData.value?.parties_with_matches || []
  })

  const selectedParty = computed((): ActaportPartyWithMatches | null => {
    if (selectedPartyIndex.value === null) return null
    return partiesWithMatches.value[selectedPartyIndex.value] || null
  })

  const handledCount = computed(() => {
    return linkSelections.value.size
  })

  const totalCount = computed(() => {
    return partiesWithMatches.value.length
  })

  const isPartyLinked = (entityUuid: string): boolean => {
    const selection = linkSelections.value.get(entityUuid)
    return selection !== undefined && selection !== 'create_new'
  }

  const isPartyNewBeteiligte = (entityUuid: string): boolean => {
    return linkSelections.value.get(entityUuid) === 'create_new'
  }

  const isMatchPreviouslyLinked = (entityUuid: string, matchId: string): boolean => {
    const previousSelection = linkSelections.value.get(entityUuid)
    return previousSelection === matchId
  }

  const canExport = computed(() => {
    // Can export if all parties have been handled or if there are no matches
    return handledCount.value === totalCount.value || totalCount.value === 0
  })

  const shouldShowButton = computed(() => {
    if (!selectedParty.value || !selectedMatchId.value) return false

    const entityUuid = selectedParty.value.entity_uuid
    const previousSelection = linkSelections.value.get(entityUuid)

    // Show button if no previous selection OR selection has changed
    return previousSelection === undefined || previousSelection !== selectedMatchId.value
  })

  const buttonText = computed(() => {
    if (!selectedParty.value) return t('link')

    const entityUuid = selectedParty.value.entity_uuid
    const previousSelection = linkSelections.value.get(entityUuid)
    const hasSelectionChanged =
      previousSelection !== undefined && previousSelection !== selectedMatchId.value

    if (selectedMatchId.value === 'create_new') {
      return hasSelectionChanged ? t('saveTheChange') : t('create')
    }

    if (hasSelectionChanged) {
      return t('saveTheChange')
    }

    return t('link')
  })

  const progressMessage = computed(() => {
    if (handledCount.value === totalCount.value && totalCount.value > 0) {
      return t('allReviewed')
    }
    return t('progressCounter', { handled: handledCount.value, total: totalCount.value })
  })
  const selectParty = (index: number) => {
    selectedPartyIndex.value = index
    const party = partiesWithMatches.value[index]

    // If party already has a selection, restore it
    const existingSelection = linkSelections.value.get(party.entity_uuid)
    if (existingSelection) {
      selectedMatchId.value = existingSelection === 'create_new' ? 'create_new' : existingSelection
    } else {
      selectedMatchId.value = null
    }
  }

  const closePanel = () => {
    selectedPartyIndex.value = null
    selectedMatchId.value = null
  }

  const selectMatch = (matchId: string | 'create_new') => {
    selectedMatchId.value = matchId
  }

  const handleLink = () => {
    if (!selectedParty.value || !selectedMatchId.value) return

    const entityUuid = selectedParty.value.entity_uuid

    // Store the selection in frontend only (not saved to backend yet)
    linkSelections.value.set(entityUuid, selectedMatchId.value)

    // Close the panel
    closePanel()
  }

  const handleExport = () => {
    emit('matching-complete', linkSelections.value)
  }

  const handleBack = () => {
    emit('back')
  }

  const loadMatches = async () => {
    if (props.matchingResult) {
      matchingData.value = props.matchingResult
      return
    }

    isLoading.value = true
    try {
      const response = await ActaportPartyMatchingService.actaportPartyMatchingFindMatchesCreate({
        requestBody: {
          case_uuid: props.caseUuid,
          participant_uuids: props.selectedParticipantUuids
        }
      })
      matchingData.value = response
    } catch (error) {
      console.error('Failed to load party matches:', error)
    } finally {
      isLoading.value = false
    }
  }

  watch(
    () => props.matchingResult,
    (newResult) => {
      if (newResult) {
        matchingData.value = newResult
      }
    }
  )

  if (!props.matchingResult) {
    loadMatches()
  }
</script>

<template>
  <div
    data-cy="party-matching-container"
    class="rounded-xl border bg-white p-6 shadow-md"
  >
    <h2
      data-cy="party-matching-title"
      class="text-xl font-bold text-gray-900"
    >
      {{ t('title') }}
    </h2>
    <p class="mt-1 text-sm text-gray-600">
      {{ t('subtitle', { count: totalCount }) }}
    </p>

    <div class="mt-4 flex items-start gap-3 rounded-lg bg-blue-50 p-4">
      <InformationCircleIcon class="h-5 w-5 flex-shrink-0 text-blue-500" />
      <p class="text-sm text-blue-700">
        {{ t('infoNote') }}
      </p>
    </div>

    <div
      v-if="isLoading"
      class="mt-6 flex items-center justify-center py-12"
    >
      <div class="text-center">
        <div
          class="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600"
        />
        <p class="mt-4 text-sm text-gray-600">{{ t('loading') }}</p>
      </div>
    </div>

    <div
      v-else-if="totalCount === 0"
      class="mt-6 rounded-lg bg-green-50 p-6 text-center"
    >
      <p class="text-green-800">{{ t('noMatchesFound') }}</p>
    </div>

    <div
      v-else
      class="mt-6 flex items-start gap-6"
      style="max-height: calc(100vh - 480px)"
    >
      <div
        class="w-1/2 space-y-3 overflow-y-auto pr-2"
        style="max-height: calc(100vh - 480px)"
      >
        <PartyCard
          v-for="(party, index) in partiesWithMatches"
          :key="party.entity_uuid"
          data-cy="party-card"
          :party="party"
          :is-selected="selectedPartyIndex === index"
          :is-linked="isPartyLinked(party.entity_uuid)"
          :is-new-beteiligte="isPartyNewBeteiligte(party.entity_uuid)"
          @select="selectParty(index)"
          @close="closePanel"
        />
      </div>

      <div
        class="flex w-1/2 flex-col"
        style="max-height: calc(100vh - 480px); background-color: #faf5ff"
      >
        <div
          v-if="selectedParty === null"
          class="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 p-8"
        >
          <div
            class="mb-4"
            style="width: 240px; height: 240px"
          >
            <PartyMatchingZeroState style="width: 100%; height: 100%" />
          </div>
          <p class="text-center text-gray-600">
            {{ t('selectParticipant') }}
          </p>
          <div class="mt-2 flex items-center justify-center gap-2">
            <!-- Checkmark icon when all reviewed -->
            <svg
              v-if="handledCount === totalCount && totalCount > 0"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.50033 10.0001L9.16699 11.6667L12.5003 8.33341M18.3337 10.0001C18.3337 14.6025 14.6027 18.3334 10.0003 18.3334C5.39795 18.3334 1.66699 14.6025 1.66699 10.0001C1.66699 5.39771 5.39795 1.66675 10.0003 1.66675C14.6027 1.66675 18.3337 5.39771 18.3337 10.0001Z"
                stroke="#6B7280"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <!-- Spinner icon when in progress -->
            <svg
              v-else
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_17239_63099)">
                <path
                  d="M9.99984 1.66675V5.00008M13.4998 6.5L15.9165 4.08333M14.9998 10.0001H18.3332M13.4998 13.5L15.9165 15.9167M9.99984 15.0001V18.3334M4.08309 15.9167L6.49976 13.5M1.6665 10.0001H4.99984M4.08309 4.08333L6.49976 6.5"
                  stroke="#6B7280"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_17239_63099">
                  <rect
                    width="20"
                    height="20"
                    fill="white"
                  />
                </clipPath>
              </defs>
            </svg>
            <p class="text-center text-sm text-gray-500">
              {{ progressMessage }}
            </p>
          </div>
        </div>

        <div
          v-else
          data-cy="match-selection-panel"
          class="flex flex-1 flex-col overflow-hidden rounded-lg border-2 transition-all"
          :style="{
            backgroundColor: '#FAF5FF',
            borderColor: selectedParty ? '#C084FC' : '#E5E7EB'
          }"
        >
          <div class="flex-shrink-0 p-4">
            <h3 class="text-center font-semibold text-gray-900">
              {{ t('potentialMatchesInActaport') }}
            </h3>
          </div>

          <div class="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            <div
              :class="[
                'cursor-pointer rounded-lg border bg-white p-4 transition-all hover:shadow-md',
                selectedMatchId === 'create_new'
                  ? 'border-purple-500 ring-2 ring-purple-200'
                  : 'border-gray-200'
              ]"
              @click="selectMatch('create_new')"
            >
              <div class="flex items-center gap-3">
                <div
                  :class="[
                    'flex h-4 w-4 items-center justify-center rounded-full border-2',
                    selectedMatchId === 'create_new'
                      ? 'border-purple-600 bg-purple-600'
                      : 'border-gray-300'
                  ]"
                >
                  <div
                    v-if="selectedMatchId === 'create_new'"
                    class="h-2 w-2 rounded-full bg-white"
                  />
                </div>
                <span class="font-medium text-gray-900">
                  {{ t('createNewBeteiligte') }}
                </span>
              </div>
            </div>

            <MatchCard
              v-for="match in selectedParty.potential_matches"
              :key="match.actaport_id"
              :match="match"
              :is-selected="selectedMatchId === match.actaport_id"
              :show-linked-badge="
                isMatchPreviouslyLinked(selectedParty.entity_uuid, match.actaport_id)
              "
              @select="selectMatch(match.actaport_id)"
            />
          </div>

          <div
            v-if="shouldShowButton"
            class="flex flex-shrink-0 justify-end border-t border-gray-200 px-4 py-4"
          >
            <button
              class="rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors
                hover:bg-purple-700"
              @click="handleLink"
            >
              {{ buttonText }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6 flex items-center justify-end gap-3 pt-6">
      <button
        class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm
          font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
        @click="handleBack"
      >
        <svg
          class="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        {{ t('back') }}
      </button>
      <button
        :disabled="!canExport"
        :class="[
          'rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-colors',
          canExport ? 'hover:bg-purple-700' : 'cursor-not-allowed opacity-50'
        ]"
        @click="handleExport"
      >
        {{ t('export') }}
      </button>
    </div>
  </div>
</template>
